import { ReactNode } from "react";
import { FileText, Plus } from "lucide-react";
import {
  Card,
  CardContent,
} from "@/shared/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { Button } from "@/shared/ui/button";

export interface DataTableColumn<T> {
  header: ReactNode;
  accessor?: keyof T;
  cell?: (item: T) => ReactNode;
  className?: string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  isLoading?: boolean;
  error?: Error | null;
  emptyTitle?: string;
  emptyDescription?: string | ((hasFilters: boolean) => string);
  emptyActionLabel?: string;
  emptyActionOnClick?: () => void;
  hasFilters?: boolean;
  loadingComponent?: ReactNode;
  errorComponent?: ReactNode;
  rowKey?: (item: T) => string;
  rowClassName?: (item: T) => string;
  errorMessage?: string;
  footer?: ReactNode;
  noCard?: boolean;
}

export function DataTable<T = any>({
  data,
  columns,
  isLoading = false,
  error = null,
  emptyTitle = "Nenhum item encontrado",
  emptyDescription,
  emptyActionLabel,
  emptyActionOnClick,
  hasFilters = false,
  loadingComponent,
  errorComponent,
  rowKey = (item: any) => (item.id ?? String(item)),
  rowClassName,
  errorMessage,
  footer,
  noCard = false,
}: DataTableProps<T>) {
  const renderContent = () => {
    // Loading State
    if (isLoading) {
      if (loadingComponent) {
        return <>{loadingComponent}</>;
      }
      return (
        <div className="p-6">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        </div>
      );
    }

    // Error State
    if (error) {
      if (errorComponent) {
        return <>{errorComponent}</>;
      }
      return (
        <div className="p-6">
          <div className="text-center text-red-400">
            <p>{errorMessage ?? `Erro ao carregar dados: ${error.message}`}</p>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              className="mt-2"
            >
              Tentar novamente
            </Button>
          </div>
        </div>
      );
    }

    // Empty State
    if (data.length === 0) {
      const description = typeof emptyDescription === "function"
        ? emptyDescription(hasFilters)
        : emptyDescription ?? (hasFilters ? "Tente ajustar os filtros" : "Comece criando um novo item");

      return (
        <div className="p-0">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-semibold">{emptyTitle}</h3>
            <p className="mb-4 text-sm text-muted-foreground">{description}</p>
            {emptyActionOnClick && !hasFilters && (
              <Button onClick={emptyActionOnClick}>
                <Plus className="mr-2 h-4 w-4" />
                {emptyActionLabel ?? "Novo item"}
              </Button>
            )}
          </div>
        </div>
      );
    }

    // Table State
    return (
      <div className="p-4">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column, index) => (
                <TableHead
                  key={index}
                  className={column.className}
                >
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item) => (
              <TableRow
                key={rowKey(item)}
                className={rowClassName?.(item)}
              >
                {columns.map((column, colIndex) => {
                  const cellContent = column.cell
                    ? column.cell(item)
                    : column.accessor
                      ? String(item[column.accessor] ?? "—")
                      : "—";

                  return (
                    <TableCell
                      key={colIndex}
                      className={column.className}
                    >
                      {cellContent}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
          {footer && (
            <TableFooter>
              {footer}
            </TableFooter>
          )}
        </Table>
      </div>
    );
  };

  if (noCard) {
    return <>{renderContent()}</>;
  }

  return (
    <Card className="bg-white/[0.03] border-white/[0.06]">
      <CardContent>{renderContent()}</CardContent>
    </Card>
  );
}

